import { createElement, useEffect, useState, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { FiSearch, FiEdit, FiEye, FiEyeOff } from "react-icons/fi";
import { IoIosAddCircleOutline } from "react-icons/io";
import { MdToggleOn, MdToggleOff } from "react-icons/md";

import { useNavigate } from "react-router-dom";

import { useAuth } from "@components/context/AuthContext";
import { BankAccountCreateEdit } from "@components/bank_account/BankAccountCreateEdit";
import { IBankAccountDTO } from "@interfaces/IBankAccountDTO";
import { Box } from "@mui/material";
import Input from "@components/Input";
import BankAccountService from "@service/BankAccountService";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";
import { formatMicrLine } from "@utils/checkFormatting";

export function BankAccountList(): JSX.Element {
  const { getIsPayrollAdmin } = useAuth();
  const navigate = useNavigate();
  const [bankAccounts, setBankAccounts] = useState<IBankAccountDTO[]>([]);
  const [selectedBankAccount, setSelectedBankAccount] =
    useState<IBankAccountDTO | null>(null);
  const [bankAccountId, setBankAccountId] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [revealedAccounts, setRevealedAccounts] = useState<
    Record<number, { account_number: string; routing_number: string }>
  >({});

  const breakpoint = 768;
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  useEffect(() => {
    async function fetchInfo() {
      const result = await getIsPayrollAdmin();
      if (!result) {
        navigate(-1);
        return;
      }
    }
    fetchInfo();
  }, [getIsPayrollAdmin, navigate]);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint, isMobile]);

  useEffect(() => {
    async function fetchBankAccounts() {
      setIsLoading(false);
      try {
        const accounts = await BankAccountService.getAllBankAccounts(
          includeInactive
        );
        setBankAccounts(accounts);
      } catch (error) {
        console.error("Error fetching bank accounts:", error);
      }
    }
    if (isLoading) {
      fetchBankAccounts();
    }
  }, [isLoading, includeInactive]);

  const pageSize = 13;
  const [gridApi, setGridApi] = useState<GridApi>();
  const defaultColDef = {
    sortable: true,
  };
  const [searchVal, setSearchVal] = useState("");

  // Context menu hook
  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});

  // Prevent default browser context menu
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  function onGridReady(params: GridReadyEvent): void {
    setGridApi(params.api);
  }

  const updateSearchQuery = (val: string): void => {
    if (gridApi !== undefined) {
      gridApi.setQuickFilter(val);
      setSearchVal(val);
    }
  };

  const handleReveal = async (accountId: number): Promise<void> => {
    if (revealedAccounts[accountId]) {
      setRevealedAccounts((prev) => {
        const next = { ...prev };
        delete next[accountId];
        return next;
      });
      return;
    }
    try {
      const data = await BankAccountService.revealBankAccountNumbers(accountId);
      setRevealedAccounts((prev) => ({
        ...prev,
        [accountId]: data,
      }));
    } catch (error) {
      console.error("Error revealing bank account numbers:", error);
    }
  };

  const handleEdit = (param: IBankAccountDTO | null): void => {
    if (param !== null) {
      setBankAccountId(param.id);
      setSelectedBankAccount(param);
      setShowEditDialog(true);
    } else {
      setBankAccountId(0);
      setSelectedBankAccount(null);
      setShowEditDialog(true);
    }
  };

  const handleToggleStatus = async (
    account: IBankAccountDTO
  ): Promise<void> => {
    try {
      await BankAccountService.toggleBankAccountStatus(account.id);
      setIsLoading(true); // Refresh the list
    } catch (error) {
      console.error("Error toggling bank account status:", error);
    }
  };

  const handleClose = () => {
    setShowEditDialog(false);
    setSelectedBankAccount(null);
    setBankAccountId(-1);
    setIsLoading(true);
  };

  const columnDefs = [
    {
      headerName: "ID",
      field: "id",
      width: 100,
      filter: true,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Label",
      field: "label",
      width: 300,
      filter: true,
    },
    {
      headerName: "Account Number",
      field: "account_number_masked",
      width: 200,
      filter: true,
      cellStyle: { textAlign: "center" },
      cellRenderer: (params: any): JSX.Element => {
        const revealed = revealedAccounts[params.data.id];
        return <span>{revealed ? revealed.account_number : params.value}</span>;
      },
    },
    {
      headerName: "Routing Number",
      field: "routing_number_masked",
      width: 200,
      filter: true,
      cellStyle: { textAlign: "center" },
      cellRenderer: (params: any): JSX.Element => {
        const revealed = revealedAccounts[params.data.id];
        return <span>{revealed ? revealed.routing_number : params.value}</span>;
      },
    },
    {
      headerName: "Next Check #",
      field: "next_check_number",
      width: 150,
      filter: true,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Status",
      field: "is_active",
      width: 120,
      filter: true,
      cellRenderer: (params: any): JSX.Element => {
        return (
          <span
            className={`tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-semibold ${
              params.value
                ? "tw-bg-green-100 tw-text-green-800"
                : "tw-bg-gray-100 tw-text-gray-800"
            }`}
          >
            {params.value ? "Active" : "Inactive"}
          </span>
        );
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Created",
      field: "created_at",
      width: 180,
      filter: true,
      valueFormatter: (params: any) => {
        if (params.value) {
          return new Date(params.value).toLocaleDateString();
        }
        return "";
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Edit",
      field: "action_edit",
      width: 80,
      cellRenderer: (params: any): JSX.Element => {
        return (
          <button
            className="tw-mt-2 tw-mb-2 tw-color-primary hover:tw-opacity-70"
            onClick={() => handleEdit(params.data)}
            title="Edit Bank Account"
          >
            {createElement(FiEdit as React.ComponentType<{ size?: number }>, {
              size: 16,
            })}
          </button>
        );
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Toggle",
      field: "action_toggle",
      width: 80,
      cellRenderer: (params: any): JSX.Element => {
        const Icon = params.data.is_active ? MdToggleOn : MdToggleOff;
        return (
          <button
            className={`tw-mt-2 tw-mb-2 hover:tw-opacity-70 ${
              params.data.is_active ? "tw-text-green-600" : "tw-text-gray-400"
            }`}
            onClick={() => handleToggleStatus(params.data)}
            title={params.data.is_active ? "Deactivate" : "Activate"}
          >
            {createElement(Icon as React.ComponentType<{ size?: number }>, {
              size: 26,
            })}
          </button>
        );
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "MICR",
      field: "action_reveal",
      width: 80,
      cellRenderer: (params: any): JSX.Element => {
        const isRevealed = !!revealedAccounts[params.data.id];
        const Icon = isRevealed ? FiEyeOff : FiEye;
        return (
          <button
            className="tw-mt-2 tw-mb-2 tw-color-primary hover:tw-opacity-70"
            onClick={() => handleReveal(params.data.id)}
            title={isRevealed ? "Hide MICR" : "Show MICR"}
          >
            {createElement(Icon as React.ComponentType<{ size?: number }>, {
              size: 16,
            })}
          </button>
        );
      },
      cellStyle: { textAlign: "center" },
    },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-w-full tw-h-full">
      <Box
        sx={{
          padding: "1em",
        }}
      >
        <div className="tw-p-4 tw-bg-white tw-rounded-lg tw-shadow-md">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
            <h1 className="tw-color-primary">Bank Accounts</h1>
            <button
              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded hover:tw-bg-blue-700 tw-transition-colors"
              onClick={() => handleEdit(null)}
            >
              {createElement(
                IoIosAddCircleOutline as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
              <span>Add Bank Account</span>
            </button>
          </div>

          <div className="tw-flex tw-gap-4 tw-mb-4 tw-items-center tw-h-[3.5vh]">
            <Input
              icon={FiSearch}
              containerStyle={{ marginRight: 4, flex: 1 }}
              id="search"
              type="text"
              name="search"
              placeholder="Search bank accounts..."
              onChange={(e: any) => updateSearchQuery(e.target.value)}
              disabled={false}
              value={searchVal}
            />
            <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => {
                  setIncludeInactive(e.target.checked);
                  setIsLoading(true);
                }}
                className="tw-w-4 tw-h-4"
              />
              <span className="tw-text-sm tw-text-gray-700">
                Show inactive accounts
              </span>
            </label>
          </div>

          {!isLoading && (
            <>
              <div className="tw-mb-4" onContextMenu={handleContextMenu}>
                {!isMobile ? (
                  <>
                    <AgGridReact
                      className="ag-theme-balham"
                      domLayout="autoHeight"
                      columnDefs={columnDefs}
                      rowHeight={40}
                      rowData={bankAccounts}
                      gridOptions={{}}
                      rowSelection="multiple"
                      defaultColDef={defaultColDef}
                      onGridReady={onGridReady}
                      onCellContextMenu={handleCellContextMenu}
                      colResizeDefault="shift"
                      animateRows
                      pagination
                      pivotPanelShow="always"
                      paginationPageSize={pageSize}
                    />
                    {ContextMenuComponent}
                  </>
                ) : (
                  <div className="tw-space-y-4">
                    {bankAccounts
                      .filter((account) =>
                        searchVal
                          ? account.label
                              .toLowerCase()
                              .includes(searchVal.toLowerCase()) ||
                            account.account_number_masked.includes(searchVal)
                          : true
                      )
                      .map((account) => (
                        <div
                          key={account.id}
                          className="tw-border tw-rounded-lg tw-p-4 tw-bg-white tw-shadow-sm"
                        >
                          <div className="tw-flex tw-justify-between tw-items-start tw-mb-2">
                            <div>
                              <h3 className="tw-font-semibold tw-text-lg">
                                {account.label}
                              </h3>
                              <p className="tw-text-sm tw-text-gray-600">
                                ID: {account.id}
                              </p>
                            </div>
                            <span
                              className={`tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-semibold ${
                                account.is_active
                                  ? "tw-bg-green-100 tw-text-green-800"
                                  : "tw-bg-gray-100 tw-text-gray-800"
                              }`}
                            >
                              {account.is_active ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div className="tw-space-y-1 tw-text-sm tw-mb-3">
                            <p>
                              <span className="tw-font-medium">Account:</span>{" "}
                              {revealedAccounts[account.id]
                                ? revealedAccounts[account.id].account_number
                                : account.account_number_masked}
                            </p>
                            <p>
                              <span className="tw-font-medium">Routing:</span>{" "}
                              {revealedAccounts[account.id]
                                ? revealedAccounts[account.id].routing_number
                                : account.routing_number_masked}
                            </p>
                            <p>
                              <span className="tw-font-medium">
                                Next Check #:
                              </span>{" "}
                              {account.next_check_number}
                            </p>
                          </div>
                          <div className="tw-flex tw-gap-2">
                            <button
                              className="tw-px-3 tw-py-1 tw-bg-amber-600 tw-text-white tw-rounded tw-text-sm hover:tw-bg-amber-700"
                              onClick={() => handleReveal(account.id)}
                            >
                              {revealedAccounts[account.id]
                                ? "Hide MICR"
                                : "Show MICR"}
                            </button>
                            <button
                              className="tw-px-3 tw-py-1 tw-bg-blue-600 tw-text-white tw-rounded tw-text-sm hover:tw-bg-blue-700"
                              onClick={() => handleEdit(account)}
                            >
                              Edit
                            </button>
                            <button
                              className={`tw-px-3 tw-py-1 tw-rounded tw-text-sm ${
                                account.is_active
                                  ? "tw-bg-gray-600 tw-text-white hover:tw-bg-gray-700"
                                  : "tw-bg-green-600 tw-text-white hover:tw-bg-green-700"
                              }`}
                              onClick={() => handleToggleStatus(account)}
                            >
                              {account.is_active ? "Deactivate" : "Activate"}
                            </button>
                          </div>
                          {revealedAccounts[account.id] && (
                            <div className="tw-mt-3 tw-p-3 tw-bg-gray-50 tw-border tw-rounded">
                              <p className="tw-text-xs tw-text-gray-500 tw-mb-1">
                                MICR Line
                              </p>
                              <div
                                style={{
                                  fontFamily: "'MICR', monospace",
                                  fontSize: "16px",
                                  letterSpacing: "0.08em",
                                  overflowX: "auto",
                                }}
                              >
                                {formatMicrLine(
                                  revealedAccounts[account.id].routing_number,
                                  revealedAccounts[account.id].account_number,
                                  account.next_check_number
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
              {Object.keys(revealedAccounts).length > 0 && (
                <div className="tw-mb-4">
                  <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2">
                    MICR Preview
                  </h3>
                  {bankAccounts
                    .filter((a) => revealedAccounts[a.id])
                    .map((account) => {
                      const revealed = revealedAccounts[account.id];
                      const micrLine = formatMicrLine(
                        revealed.routing_number,
                        revealed.account_number,
                        account.next_check_number
                      );
                      return (
                        <div
                          key={account.id}
                          className="tw-border tw-rounded tw-p-4 tw-mb-2 tw-bg-gray-50"
                        >
                          <p className="tw-text-xs tw-text-gray-500 tw-mb-1">
                            {account.label}
                          </p>
                          <div
                            style={{
                              fontFamily: "'MICR', monospace",
                              fontSize: "24px",
                              letterSpacing: "0.08em",
                              padding: "8px 12px",
                              backgroundColor: "#fff",
                              border: "1px dashed #ccc",
                              borderRadius: "4px",
                            }}
                          >
                            {micrLine}
                          </div>
                          <p className="tw-text-xs tw-text-gray-400 tw-mt-1">
                            C = On-Us (⑈) &nbsp; A = Transit (⑆) &nbsp; Check #
                            {account.next_check_number}
                          </p>
                        </div>
                      );
                    })}
                </div>
              )}
              {showEditDialog && (
                <BankAccountCreateEdit
                  id={bankAccountId}
                  selectedBankAccount={selectedBankAccount}
                  isOpen={showEditDialog}
                  onClose={handleClose}
                />
              )}
            </>
          )}

          {isLoading && (
            <div className="tw-flex tw-justify-center tw-items-center tw-py-8">
              <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-blue-600"></div>
            </div>
          )}
        </div>
      </Box>
    </div>
  );
}
